"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHumidityAlerts = exports.determineHumiditySeverity = void 0;
const react_1 = require("react");
const mutations_1 = require("../../graphql/mutations");
const queries_1 = require("../../graphql/queries");
const types_1 = require("../../types");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const determineHumiditySeverity = (alertSeverity) => {
    switch (alertSeverity) {
        case types_1.SensorAlertSeverity.Critical:
            return types_1.SensorAlertSeverity.Critical;
        case types_1.SensorAlertSeverity.High:
            return types_1.SensorAlertSeverity.High;
        case types_1.SensorAlertSeverity.Medium:
            return types_1.SensorAlertSeverity.Medium;
        case types_1.SensorAlertSeverity.Low:
            return types_1.SensorAlertSeverity.Low;
        default:
            return types_1.SensorAlertSeverity.Medium; // Default fallback
    }
};
exports.determineHumiditySeverity = determineHumiditySeverity;
const useHumidityAlerts = ({ sensors, sensorAlerts, alertsLoading, translateFn, fetchAndUpdateSensorAlerts, }) => {
    const [alertState, setAlertState] = (0, react_1.useState)(types_1.HumidityAlertState.NoAlert);
    const [alertSeverity, setAlertSeverity] = (0, react_1.useState)(null);
    const [alertMessage, setAlertMessage] = (0, react_1.useState)('');
    const [alertType, setAlertType] = (0, react_1.useState)(null);
    const [location, setLocation] = (0, react_1.useState)('');
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [requestingService, setRequestingService] = (0, react_1.useState)(false);
    const [activeAlert, setActiveAlert] = (0, react_1.useState)(null);
    const [skipNextProcess, setSkipNextProcess] = (0, react_1.useState)(false);
    const primaryHumiditySensor = (0, react_1.useMemo)(() => {
        // Humidity readings come from the leak detector sensor
        const humiditySensors = sensors.filter((sensor) => sensor.type === types_1.SensorType.LEAK_DETECTOR);
        return humiditySensors[0] || null;
    }, [sensors]);
    const getServiceRequestState = async (serviceRequestId) => {
        try {
            const serviceResponse = await amplifyBootstrap_1.API.graphql({
                query: queries_1.getServiceRequest,
                variables: { id: serviceRequestId },
            });
            if (serviceResponse?.data?.getServiceRequest) {
                const serviceRequest = serviceResponse.data.getServiceRequest;
                const status = serviceRequest.status?.toLowerCase();
                switch (status) {
                    case 'in_progress':
                        return {
                            state: types_1.HumidityAlertState.InProgress,
                            message: translateFn('humidity.serviceInProgress'),
                        };
                    case 'open':
                    case 'scheduled':
                        return {
                            state: types_1.HumidityAlertState.ServiceScheduled,
                            message: translateFn('humidity.serviceScheduled'),
                        };
                    case 'pending':
                    case 'requested':
                        return {
                            state: types_1.HumidityAlertState.ServiceRequested,
                            message: translateFn('humidity.serviceRequested'),
                        };
                    case 'closed':
                    case 'canceled':
                        // When service request is closed or canceled, return null to indicate
                        // we should not use service request state and let processAlerts determine
                        // the appropriate state based on alert acknowledgment
                        return null;
                    default:
                        return {
                            state: types_1.HumidityAlertState.ServiceRequested,
                            message: translateFn('humidity.serviceRequested'),
                        };
                }
            }
        }
        catch (error) {
            console.error('Failed to fetch service request details:', error);
        }
        return null;
    };
    (0, react_1.useEffect)(() => {
        const processAlerts = async () => {
            if (alertsLoading || sensors.length === 0)
                return;
            // Skip processing if we just manually set the state
            if (skipNextProcess) {
                setSkipNextProcess(false);
                return;
            }
            const now = Date.now();
            const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000;
            const alert = sensorAlerts.find((alert) => {
                // Only show high humidity alerts for now, not low humidity
                const isHumidityAlert = alert.alertType === types_1.SensorAlertType.HighHumidity;
                const isActiveStatus = alert.status === types_1.AlertStatus.NEW ||
                    alert.status === types_1.AlertStatus.ONGOING ||
                    alert.status === types_1.AlertStatus.PENDING ||
                    alert.status === types_1.AlertStatus.ACTIVE;
                const isUnacknowledged = alert.acknowledgedAt === null && alert.acknowledgedBy === null;
                const isRecent = alert.timestamp && alert.timestamp * 1000 >= twentyFourHoursAgo;
                return isHumidityAlert && isActiveStatus && isUnacknowledged && isRecent;
            });
            if (alert) {
                setActiveAlert(alert);
                const severity = (0, exports.determineHumiditySeverity)(alert.severity);
                setAlertSeverity(severity);
                setAlertType(alert.alertType);
                const sensor = sensors.find((s) => s.id === alert.sensorId);
                const sensorLocation = sensor?.location || translateFn('humidity.unknownLocation');
                setLocation(sensorLocation);
                // Check if there's a service request linked to this alert
                try {
                    const alertResponse = await amplifyBootstrap_1.API.graphql({
                        query: queries_1.getSensorAlert,
                        variables: { id: alert.id },
                    });
                    if (alertResponse?.data?.getSensorAlert) {
                        const fullAlert = alertResponse.data.getSensorAlert;
                        // If there's a service request linked to the alert, check its status
                        if (fullAlert.serviceRequest && fullAlert.serviceRequest.id) {
                            const serviceState = await getServiceRequestState(fullAlert.serviceRequest.id);
                            if (serviceState) {
                                setAlertState(serviceState.state);
                                setAlertMessage(serviceState.message);
                                return;
                            }
                        }
                    }
                }
                catch (alertFetchError) {
                    console.error('Failed to fetch alert details:', alertFetchError);
                }
                // No service request or failed to fetch, show alert based on severity
                let specificState;
                let messageKey = '';
                if (severity === types_1.SensorAlertSeverity.Critical) {
                    specificState = types_1.HumidityAlertState.HumidityExtremeAlert;
                    messageKey = 'humidity.extremeHumidityDetectedIn';
                }
                else if (severity === types_1.SensorAlertSeverity.High) {
                    specificState = types_1.HumidityAlertState.HumidityHighAlert;
                    messageKey = 'humidity.highHumidityDetectedIn';
                }
                else {
                    specificState = types_1.HumidityAlertState.HumidityCautionAlert;
                    messageKey = 'humidity.cautionaryHumidityDetectedIn';
                }
                setAlertState(specificState);
                const message = translateFn(messageKey, { location: sensorLocation });
                setAlertMessage(message);
            }
            else {
                setActiveAlert(null);
                setAlertState(types_1.HumidityAlertState.NoAlert);
                setAlertSeverity(null);
                setAlertType(null);
                setLocation('');
                setAlertMessage('');
            }
        };
        processAlerts();
    }, [sensorAlerts.length, alertsLoading, sensors.length, sensorAlerts, sensors, translateFn]);
    const requestService = async (input) => {
        setRequestingService(true);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.requestHumidityService,
                variables: { input },
            });
            if (response?.data?.requestHumidityService) {
                const serviceRequest = response.data.requestHumidityService;
                if (activeAlert) {
                    try {
                        await new Promise((resolve) => setTimeout(resolve, 500));
                        const alertResponse = await amplifyBootstrap_1.API.graphql({
                            query: queries_1.getSensorAlert,
                            variables: { id: activeAlert.id },
                        });
                        if (alertResponse?.data?.getSensorAlert) {
                            const updatedAlert = alertResponse.data.getSensorAlert;
                            // Check for serviceRequest object (not serviceRequestId)
                            if (updatedAlert.serviceRequest && updatedAlert.serviceRequest.id) {
                                const serviceState = await getServiceRequestState(updatedAlert.serviceRequest.id);
                                if (serviceState) {
                                    setAlertState(serviceState.state);
                                    setAlertMessage(serviceState.message);
                                }
                            }
                            else {
                                // Backend didn't link them - manually set state
                                setAlertState(types_1.HumidityAlertState.ServiceRequested);
                                setAlertMessage(translateFn('humidity.serviceRequested'));
                                setSkipNextProcess(true); // Prevent useEffect from overwriting this state
                            }
                        }
                    }
                    catch (alertFetchError) {
                        console.error('Failed to fetch updated alert details:', alertFetchError);
                        // Fallback to manual state
                        setAlertState(types_1.HumidityAlertState.ServiceRequested);
                        setAlertMessage(translateFn('humidity.serviceRequested'));
                        setSkipNextProcess(true); // Prevent useEffect from overwriting this state
                    }
                }
                // Refetch all alerts for the sensor
                if (primaryHumiditySensor?.id) {
                    await fetchAndUpdateSensorAlerts(primaryHumiditySensor.id);
                }
                return serviceRequest;
            }
            return null;
        }
        catch (error) {
            console.error('Failed to request humidity service:', error);
            setAlertMessage(translateFn('humidity.failedToRequestService'));
            return null;
        }
        finally {
            setRequestingService(false);
        }
    };
    const clearHumidityAlertFromDevice = async (sensorId, hubId) => {
        setLoading(true);
        try {
            const response = await amplifyBootstrap_1.API.graphql({
                query: mutations_1.clearHumidityAlert,
                variables: { sensorId, hubId },
            });
            if (response?.data?.clearHumidityAlert) {
                await fetchAndUpdateSensorAlerts(sensorId);
                return true;
            }
            return false;
        }
        catch (error) {
            console.error('Failed to clear humidity alert:', error);
            setAlertMessage(translateFn('humidity.failedToClearAlert'));
            return false;
        }
        finally {
            setLoading(false);
        }
    };
    const clearAlert = async () => {
        setLoading(true);
        try {
            if (activeAlert) {
                const response = await amplifyBootstrap_1.API.graphql({
                    query: mutations_1.updateSensorAlertAcknowledgedStateAnonymous,
                    variables: {
                        input: {
                            id: activeAlert.id,
                            isAcknowledged: true,
                        },
                    },
                });
                if (response?.data?.updateSensorAlertAcknowledgedStateAnonymous) {
                    setAlertState(types_1.HumidityAlertState.NoAlert);
                    setAlertSeverity(null);
                    setAlertType(null);
                    setAlertMessage('');
                    setActiveAlert(null);
                    if (primaryHumiditySensor?.id) {
                        await fetchAndUpdateSensorAlerts(primaryHumiditySensor.id);
                    }
                }
                else {
                    console.error('Failed to acknowledge alert - no data returned');
                    setAlertMessage(translateFn('humidity.failedToClearAlert'));
                }
            }
        }
        catch (error) {
            console.error('Failed to acknowledge alert:', error);
            setAlertMessage(translateFn('humidity.failedToClearAlert'));
        }
        finally {
            setLoading(false);
        }
    };
    return {
        alertState,
        alertSeverity,
        alertMessage,
        alertType,
        activeAlert,
        primaryHumiditySensor,
        location,
        loading: loading || alertsLoading,
        requestingService,
        requestService,
        clearAlert,
        clearHumidityAlertFromDevice,
    };
};
exports.useHumidityAlerts = useHumidityAlerts;
